import { keepPreviousData, useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { useScopedQueryKey } from '../../hooks/useScopedQueryKey';
import { useSlashSearchShortcut } from '../../hooks/useSlashSearchShortcut';
import { formatPhone } from '../../shared/phone';
import { describeWhen, formatShortDateTime } from '../../shared/time';
import {
  Avatar,
  HUE_TINT,
  Callout,
  EmptyState,
  IconCalendar,
  IconExternal,
  IconPlug,
  IconSearch,
  IconUsers,
  Input,
  Kbd,
  Pagination,
  Skeleton,
  StatusPill,
  SummaryFact,
  SummaryFacts,
  Table,
  TableSkeletonRows,
  Tag,
  TBody,
  TD,
  TH,
  THead,
  TR,
  When,
} from '../../ui/v2';
import {
  fetchReservationsIntegrationStatus,
  reservationsStatusHasActiveConnection,
} from '../../utils/reservationsIntegrationStatus';
import { phoneGlyph } from '../SmsInbox/smsModel';
import ReservationsList from './ReservationsHeader';
import {
  DEFAULT_OPENTABLE_ORDERING,
  fetchOpenTableReservationsPage,
  formatGuestName,
  nextOrdering,
  OPENTABLE_SORT_FIELD_TO_UI,
  OPENTABLE_SORT_UI_TO_FIELD,
  openTableStatusTone,
  parseOrdering,
  RESERVATIONS_PAGE_SIZE,
  reservationOrigin,
  sentenceCaseStatus,
} from './reservationModel';

const GUEST_CENTER_URL = 'https://guestcenter.opentable.com/';

/**
 * Reading order: who is coming, how to reach them, when, how many, where
 * the booking stands, what they asked for. Booking-system identifiers come
 * after, in quieter type.
 */
const COLUMNS: {
  key: string;
  label: string;
  width?: number;
  align?: 'left' | 'right';
  sortable?: boolean;
}[] = [
  { key: 'guest', label: 'Guest' },
  { key: 'phone', label: 'Phone', width: 140 },
  { key: 'start_time', label: 'Reservation time', width: 196, sortable: true },
  { key: 'party', label: 'Party', width: 88 },
  { key: 'status', label: 'Status', width: 136, sortable: true },
  { key: 'request', label: 'Request', width: 184 },
  { key: 'source', label: 'Source', width: 96 },
  { key: 'confirmation', label: 'Confirmation', width: 120 },
  { key: 'reservation_id', label: 'Reservation ID', width: 152 },
  { key: 'customer_id', label: 'Customer ID', width: 108, sortable: true },
  { key: 'email', label: 'Guest email', width: 200 },
  { key: 'updated_at', label: 'Last updated', width: 148, sortable: true },
];

const MissingValue: React.FC = () => <span className="text-v2-fg-4">—</span>;

/** Party size as people, not a bare integer. */
const PartyCell: React.FC<{
  size?: number | string | null;
  muted?: boolean;
}> = ({ size, muted }) =>
  size ? (
    <span
      className={clsx(
        'v2-num inline-flex h-6 items-center gap-1 rounded-v2 px-1.5 text-v2-12 font-semibold',
        muted ? 'bg-v2-sunken text-v2-fg-4' : HUE_TINT.rose,
      )}
      aria-label={`Party of ${size}`}
    >
      <IconUsers size={12} />
      {size}
    </span>
  ) : (
    <MissingValue />
  );

const ConnectOpenTable: React.FC = () => (
  <EmptyState
    icon={<IconPlug />}
    title="Connect OpenTable to see reservations here"
    description="Link your OpenTable account so AskArthur can manage phone reservations automatically."
    action={
      <div className="flex flex-col items-center gap-4">
        <ol className="space-y-1.5 text-left text-v2-13 text-v2-fg-2">
          <li className="flex items-baseline gap-2">
            <span className="v2-num text-v2-fg-3">1.</span>
            <span>
              Go to{' '}
              <a
                href={GUEST_CENTER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-v2-accent hover:underline"
              >
                OpenTable Guest Center
              </a>
            </span>
          </li>
          <li className="flex items-baseline gap-2">
            <span className="v2-num text-v2-fg-3">2.</span>
            Navigate to the Integrations tab
          </li>
          <li className="flex items-baseline gap-2">
            <span className="v2-num text-v2-fg-3">3.</span>
            Select Phone Automation and select Ask Arthur
          </li>
        </ol>
        <a
          href={GUEST_CENTER_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-v2-13 font-medium text-v2-accent hover:underline"
        >
          Go to OpenTable Guest Center
          <IconExternal size={14} />
        </a>
      </div>
    }
  />
);

const OpenTableReservationsView: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const orderingParam =
    searchParams.get('ordering') ?? DEFAULT_OPENTABLE_ORDERING;
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const searchRef = useRef<HTMLInputElement>(null);

  const { sortKey, sortOrder } = useMemo(
    () =>
      parseOrdering(orderingParam, OPENTABLE_SORT_FIELD_TO_UI, 'updated_at'),
    [orderingParam],
  );

  const queryKey = useScopedQueryKey(
    'opentable-reservations',
    currentPage,
    RESERVATIONS_PAGE_SIZE,
    orderingParam,
    debouncedSearch,
  );
  const { data, isPending, isError, error, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchOpenTableReservationsPage(
        currentPage,
        orderingParam,
        debouncedSearch,
      ),
    placeholderData: keepPreviousData,
  });
  const { data: reservationsStatus, isPending: isConnectionStatusPending } =
    useQuery({
      queryKey: useScopedQueryKey('reservations-integration-status'),
      queryFn: fetchReservationsIntegrationStatus,
    });
  const hasActiveConnection =
    reservationsStatusHasActiveConnection(reservationsStatus);

  const reservations = data?.reservations ?? [];
  const totalPages = data?.totalPages ?? 1;
  const totalCount = data?.totalCount ?? 0;
  const averagePartySize = data?.averagePartySizeDisplay ?? '—';
  const showInitialLoading = isPending && reservations.length === 0;
  const fetchError =
    isError && error instanceof Error
      ? error.message
      : isError
        ? 'Failed to load OpenTable reservations.'
        : null;
  const showLoading =
    showInitialLoading ||
    (reservations.length === 0 &&
      !debouncedSearch &&
      isConnectionStatusPending);
  const isRefreshing =
    isFetching && !showInitialLoading && !fetchError && reservations.length > 0;
  const showPagination =
    !showInitialLoading && !fetchError && reservations.length > 0;
  const showCount = showInitialLoading || totalCount > 0;
  const showAveragePartySize =
    showInitialLoading ||
    (averagePartySize !== '—' && averagePartySize.trim() !== '');

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useSlashSearchShortcut(searchRef);

  const toggleSort = useCallback(
    (key: string) => {
      const targetField = OPENTABLE_SORT_UI_TO_FIELD[key];
      if (!targetField) return;
      setCurrentPage(1);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set(
          'ordering',
          nextOrdering(
            prev.get('ordering') ?? DEFAULT_OPENTABLE_ORDERING,
            targetField,
          ),
        );
        return next;
      });
    },
    [setSearchParams],
  );

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
    setCurrentPage(1);
  };

  let body: React.ReactNode;
  if (fetchError && !showLoading) {
    body = (
      <div className="p-4 lg:p-6">
        <Callout tone="danger" title="Couldn't load OpenTable reservations">
          {fetchError}
        </Callout>
      </div>
    );
  } else if (!showLoading && reservations.length === 0) {
    body = debouncedSearch ? (
      <EmptyState
        icon={<IconSearch />}
        title="You don't have any matching OpenTable reservations at the moment"
        description="When matching OpenTable reservations appear, they'll show up here."
      />
    ) : hasActiveConnection ? (
      <EmptyState
        icon={<IconCalendar />}
        title="No reservations yet"
        description="New OpenTable reservations will appear here automatically."
      />
    ) : (
      <ConnectOpenTable />
    );
  } else {
    body = (
      <>
        {showLoading && (
          <p role="status" className="sr-only">
            Loading OpenTable reservations…
          </p>
        )}
        <div
          className={clsx(
            'v2-scroll v2-scroll-x transition-opacity',
            isRefreshing && 'opacity-70',
          )}
        >
          <Table fixed className="min-w-[1800px]">
            <THead>
              <tr>
                {COLUMNS.map((column) => (
                  <TH
                    key={column.key}
                    width={column.width}
                    align={column.align}
                    sortable={column.sortable && !showLoading}
                    active={column.sortable && sortKey === column.key}
                    dir={sortOrder}
                    onSort={() => toggleSort(column.key)}
                  >
                    {column.label}
                  </TH>
                ))}
              </tr>
            </THead>
            <TBody>
              {showLoading ? (
                <TableSkeletonRows cols={COLUMNS.length} size="lg" />
              ) : (
                reservations.map((reservation, idx) => {
                  const guestName = formatGuestName(reservation);
                  const origin = reservationOrigin(reservation.origin_channel);
                  const tone = openTableStatusTone(reservation.status);
                  // Cancelled bookings stay in the list but step back.
                  const muted = tone === 'danger';
                  const isToday =
                    !muted &&
                    describeWhen(reservation.start_time)?.day === 'today';
                  return (
                    <TR
                      size="lg"
                      key={`${reservation.reservation_id}-${idx}`}
                      className={clsx(isToday && 'bg-v2-warning/[0.05]')}
                      data-today={isToday || undefined}
                    >
                      <TD size="lg" className="relative overflow-hidden">
                        {isToday && (
                          <span
                            aria-hidden
                            className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-sm bg-v2-warning"
                          />
                        )}
                        <div className="flex min-w-0 items-center gap-2">
                          <Avatar
                            name={guestName}
                            identity={reservation.guest_phone_number}
                            fallback={phoneGlyph(
                              reservation.guest_phone_number,
                            )}
                            hue="auto"
                            className={clsx('mr-0.5', muted && 'opacity-50')}
                          />
                          {guestName ? (
                            <span
                              className={clsx(
                                'truncate text-v2-14',
                                muted
                                  ? 'font-medium text-v2-fg-3'
                                  : 'font-semibold text-v2-fg',
                              )}
                            >
                              {guestName}
                            </span>
                          ) : (
                            <span className="truncate text-v2-fg-3">
                              Unknown guest
                            </span>
                          )}
                          {reservation.is_vip && (
                            <span
                              className="shrink-0"
                              title={
                                reservation.vip_name
                                  ? `VIP: ${reservation.vip_name}`
                                  : 'VIP guest'
                              }
                            >
                              <Tag tone="warning">VIP</Tag>
                            </span>
                          )}
                        </div>
                      </TD>
                      <TD
                        size="lg"
                        num
                        className={clsx(
                          'whitespace-nowrap',
                          muted ? 'text-v2-fg-4' : 'text-v2-fg-2',
                        )}
                      >
                        {reservation.guest_phone_number ? (
                          formatPhone(reservation.guest_phone_number)
                        ) : (
                          <MissingValue />
                        )}
                      </TD>
                      <TD size="lg" num className="whitespace-nowrap">
                        <When
                          value={reservation.start_time}
                          muted={muted}
                          stacked
                        />
                      </TD>
                      <TD size="lg">
                        <PartyCell
                          size={reservation.party_size}
                          muted={muted}
                        />
                      </TD>
                      <TD size="lg">
                        {reservation.status ? (
                          <StatusPill tone={tone}>
                            {sentenceCaseStatus(reservation.status)}
                          </StatusPill>
                        ) : (
                          <MissingValue />
                        )}
                      </TD>
                      <TD
                        size="lg"
                        truncate
                        className={clsx(
                          muted ? 'text-v2-fg-4' : 'text-v2-fg-2',
                        )}
                      >
                        {reservation.special_request ? (
                          <span title={reservation.special_request}>
                            {reservation.special_request}
                          </span>
                        ) : (
                          <MissingValue />
                        )}
                      </TD>
                      <TD size="lg">
                        {origin ? (
                          <span title={origin.title}>
                            <Tag tone={origin.tone}>{origin.label}</Tag>
                          </span>
                        ) : (
                          <MissingValue />
                        )}
                      </TD>
                      <TD
                        size="lg"
                        num
                        truncate
                        className="text-v2-12 text-v2-fg-2"
                      >
                        {reservation.confirmation_code || <MissingValue />}
                      </TD>
                      <TD
                        size="lg"
                        num
                        truncate
                        className="text-v2-12 text-v2-fg-3"
                      >
                        {reservation.reservation_id ? (
                          <span title={String(reservation.reservation_id)}>
                            {reservation.reservation_id}
                          </span>
                        ) : (
                          <MissingValue />
                        )}
                      </TD>
                      <TD size="lg" num className="text-v2-12 text-v2-fg-3">
                        {reservation.customer_id || <MissingValue />}
                      </TD>
                      <TD size="lg" truncate className="text-v2-12">
                        {reservation.guest_email ? (
                          <a
                            href={`mailto:${reservation.guest_email}`}
                            className="text-v2-accent hover:underline"
                          >
                            {reservation.guest_email}
                          </a>
                        ) : (
                          <MissingValue />
                        )}
                      </TD>
                      <TD
                        size="lg"
                        num
                        className={clsx(
                          'whitespace-nowrap text-v2-12 text-v2-fg-3',
                        )}
                      >
                        {formatShortDateTime(reservation.updated_at) || (
                          <MissingValue />
                        )}
                      </TD>
                    </TR>
                  );
                })
              )}
            </TBody>
          </Table>
        </div>
        {showPagination && (
          <Pagination
            page={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={RESERVATIONS_PAGE_SIZE}
            onPage={setCurrentPage}
            noun="reservations"
          />
        )}
      </>
    );
  }

  return (
    <ReservationsList
      refreshLabel={isRefreshing ? 'Updating OpenTable reservations...' : null}
      summary={
        (showCount || showAveragePartySize) && (
          <SummaryFacts>
            {showCount && (
              <SummaryFact
                label="Reservations"
                value={
                  showInitialLoading ? (
                    <Skeleton width={48} className="h-7" />
                  ) : (
                    totalCount.toLocaleString()
                  )
                }
              />
            )}
            {showAveragePartySize && (
              <SummaryFact
                label="Avg. party size"
                value={
                  showInitialLoading ? (
                    <Skeleton width={32} className="h-7" />
                  ) : (
                    averagePartySize
                  )
                }
              />
            )}
          </SummaryFacts>
        )
      }
      toolbar={
        <div className="w-full min-w-0 sm:w-[360px] sm:shrink">
          <Input
            ref={searchRef}
            size="lg"
            id="opentable-reservations-search"
            type="search"
            aria-label="Search by name or phone"
            placeholder="Find a guest by name or phone…"
            value={searchInput}
            onChange={handleSearchChange}
            leading={<IconSearch size={16} />}
            trailing={
              <span className="hidden sm:inline-flex">
                <Kbd>/</Kbd>
              </span>
            }
          />
        </div>
      }
    >
      {body}
    </ReservationsList>
  );
};

export default OpenTableReservationsView;
